import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

// --------------------------------------------------
// FETCH ALL ORDERS (admin)
// --------------------------------------------------
export const fetchAllOrders = createAsyncThunk(
  "adminOrders/fetchAllOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/orders`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
        },
      });

      // Support two response shapes:
      // 1) { orders: [...] }  or 2) [ ... ]
      const payload = response.data;
      if (!payload) return []; // safe fallback
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.orders)) return payload.orders;
      // last fallback: if payload has other useful array, try it
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch orders" });
    }
  }
);

// --------------------------------------------------
// UPDATE ORDER STATUS
// expects payload { id, status, trackingLink?, trackingNumber?, cancellationReason? }
// --------------------------------------------------
export const updateOrderStatus = createAsyncThunk(
  "adminOrders/updateOrderStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const { id, status, trackingLink, trackingNumber, cancellationReason } = payload;
      const body = { status };
      if (trackingLink) body.trackingLink = trackingLink;
      if (trackingNumber) body.trackingNumber = trackingNumber;
      if (cancellationReason) body.cancellationReason = cancellationReason;

      const response = await axios.put(
        `${BACKEND_URL}/api/admin/orders/${id}`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );

      // backend might return { order: {...} } or the order directly
      const data = response.data;
      if (!data) return null;
      if (data.order) return data.order;
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to update order" });
    }
  }
);

// --------------------------------------------------
// DELETE ORDER
// --------------------------------------------------
export const deleteOrder = createAsyncThunk(
  "adminOrders/deleteOrder",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/orders/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
        },
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to delete order" });
    }
  }
);

// --------------------------------------------------
// SLICE
// --------------------------------------------------
const adminOrderSlice = createSlice({
  name: "adminOrders",
  initialState: {
    orders: [],
    selectedOrder: null,
    totalOrders: 0,
    totalSales: 0,
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
    clearAdminError: (state) => {
      state.error = null;
    },
    clearAdminSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchAllOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllOrders.fulfilled, (state, action) => {
        state.loading = false;
        const orders = Array.isArray(action.payload) ? action.payload : [];
        state.orders = orders;
        state.totalOrders = orders.length;
        state.totalSales = orders.reduce((acc, o) => acc + (o.totalPrice || 0), 0);
      })
      .addCase(fetchAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch orders";
      })

      // UPDATE
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        const updated = action.payload;
        if (!updated || !updated._id) {
          // nothing to replace
          return;
        }

        const idx = state.orders.findIndex((o) => o._id === updated._id);
        if (idx !== -1) {
          state.orders[idx] = updated;
        } else {
          // if order not present, prepend it (safe fallback)
          state.orders.unshift(updated);
        }

        // recompute totals safely
        state.totalOrders = state.orders.length;
        state.totalSales = state.orders.reduce((acc, o) => acc + (o.totalPrice || 0), 0);
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update order";
      })

      // DELETE
      .addCase(deleteOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.filter((o) => o._id !== action.payload);
        state.totalOrders = state.orders.length;
        state.totalSales = state.orders.reduce((acc, o) => acc + (o.totalPrice || 0), 0);
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete order";
      });
  },
});

export const { setSelectedOrder, clearSelectedOrder, clearAdminError, clearAdminSuccess } =
  adminOrderSlice.actions;

export default adminOrderSlice.reducer;
